import { useEffect, useMemo, useState } from 'react';
import styles from './CreditSummaryTable.module.scss';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { fetchCreditBureauSummaryTable } from '../../../../../../store/dashboard/creditBureau/actions';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SortIcon from '../../../../../Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import { SortConfig } from '../../../../../../utils/constants/commonConstants';
import { DataTableColumn } from '../../../../../../common/_custom/DataTable/DataTable.types';
import { buildCreditSummaryColumns, CREDIT_SUMMARY_FILTER_SOURCE_KEY, isSortableColumn } from './CreditSummaryTable.constants';
import useCreditSummaryRows, { nextSortConfig } from './useCreditSummaryRows';

type Header = {
    name: string;
    key: string;
};

type Row = {
    name?: string;
    investorName?: string;
    loanType?: string;
    accountStatus?: string;
    lastReported?: string;
    sanctionDate?: string;
    sanctionedLimit?: string;
    outstandingAmount?: string;
    currentOverdue?: string;
    emi?: string;
    tenure?: string;
    frequency?: string;
    accountRemarks?: string;
    ownership?: string;
    repaymentHistory?: string;
    // Raw sanction date polus already serializes alongside the formatted string — preferred as the
    // sort key for that column so we never parse a display value we don't have to.
    sanctionedDateActual?: string;
};

type TableData = {
    headers: Header[];
    cibilData: Row[];
};

type CreditSummaryTableProps = {
    assesseeOrgId?: string;
    /**
     * Publishes the resolved filter columns to the parent, which owns the filter trigger and drawer
     * so they can sit in the report header beside the Chart/Table toggle. Applied filter VALUES are
     * deliberately not passed down — both sides read them from redux under
     * CREDIT_SUMMARY_FILTER_SOURCE_KEY, so there is no filter state to keep in sync.
     */
    onColumnsResolved?: (columns: DataTableColumn[]) => void;
};

function CreditSummaryTable({ assesseeOrgId, onColumnsResolved }: CreditSummaryTableProps) {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState<TableData | null>(null);
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
    const [searchParams, setSearchParams] = useSearchParams();
    const directorId = searchParams.get('directorId');
    const bureauType = searchParams.get('bureauType') == 'All' ? null : searchParams.get('bureauType');

    const directorBureauType = searchParams.get('directorBureauType');

    const appliedFilters = useAppSelector((state) => state.investor.customFilterDrawer?.appliedFilters?.[CREDIT_SUMMARY_FILTER_SOURCE_KEY]);

    useEffect(() => {
        assesseeOrgId && fetchTableData(assesseeOrgId);
    }, [assesseeOrgId]);

    /* Takes the org id as an argument rather than closing over the optional prop, so the payload
       field is narrowed to a string at the call site's existing guard. */
    const fetchTableData = (orgId: string) => {
        setLoading(true);
        dispatch(
            fetchCreditBureauSummaryTable(
                {
                    assesseeOrgId: orgId,
                    ...(directorId && { directorId: directorId }),
                    ...((bureauType || directorBureauType) && { type: bureauType ?? directorBureauType }),
                },
                {
                    onSuccess: (data) => {
                        setLoading(false);
                        setTableData(data);
                    },
                    onError: () => {
                        setLoading(false);
                    },
                },
            ),
        );
    };

    /* Filter fields are derived from the headers polus sent, with tick-list options read out of the
       rows themselves — so the drawer can never offer a column this table doesn't have, nor a value
       that matches no row. */
    const filterColumns = useMemo(
        () => buildCreditSummaryColumns(tableData?.headers, tableData?.cibilData || []),
        [tableData?.headers, tableData?.cibilData],
    );

    useEffect(() => {
        onColumnsResolved?.(filterColumns);
    }, [filterColumns, onColumnsResolved]);

    const { displayRows } = useCreditSummaryRows(tableData?.cibilData, appliedFilters, sortConfig);

    const onSort = (field: string) => setSortConfig((current) => nextSortConfig(current, field));

    return (
        <div className={styles.CreditSummaryTablePage}>
            <div className={styles.TableContainer}>
                {loading ? (
                    <div className={styles.Loading}>
                        <LoadingSpinner height="40px" color="#6021b3" />
                    </div>
                ) : (
                    <>
                        {tableData?.headers?.length && displayRows?.length ? (
                            <table className={styles.CreditSummaryTable}>
                                <thead className={styles.TableHead}>
                                    <tr className={styles.TableHeadRow}>
                                        {tableData?.headers?.map((header, i) => (
                                            <td
                                                key={header?.key || i}
                                                className={clsx(styles.TableHeadRowItem, {
                                                    [styles.SecondHeader]: i === 1,
                                                    [styles.ThirdHeader]: !directorId && i === 2,
                                                })}
                                            >
                                                {isSortableColumn(header?.key) ? (
                                                    <span className={styles.SortableHeader}>
                                                        {header?.name}
                                                        <SortIcon
                                                            field={header.key}
                                                            sortConfig={sortConfig}
                                                            onSort={onSort}
                                                            alwaysVisible
                                                        />
                                                    </span>
                                                ) : (
                                                    header?.name || 'no header'
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className={styles.TableBody}>
                                    {displayRows?.map((row, rowIndex) => (
                                        <tr className={styles.TableBodyRow} key={rowIndex}>
                                            {tableData?.headers?.map((header, i) => (
                                                <td
                                                    key={header?.key || i}
                                                    className={clsx(styles.TableBodyRowItem, {
                                                        [styles.SecondColumn]: i === 1,
                                                        [styles.ThirdColumn]: !directorId && i === 2,
                                                    })}
                                                >
                                                    {row?.[header?.key] || ''}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.NoData}>No Data Available</div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default CreditSummaryTable;
